import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  db,
  createMessageToTask,
  changeTaskName,
  uploadTaskImage,
  removeTaskImage,
} from "../../firebase/firebase.utils";

import "./large-task.styles.scss";

import LoadModule from "../modules/load-module.component.jsx/load-module.component";
import RetroButton from "../retro/button/retro-button.component";
import TaskDescription from "../task-description/task-description.component";
import Avatar from "../retro/avatar/avatar.component";
import Message from "../message/message.component";
import Loading from "../retro/loading/loading.component";
import Subtasks from "../dnd/subtasks/subtasks.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faTrash } from "@fortawesome/free-solid-svg-icons";

const LargeTask = ({ task }) => {
  const moduleData = useSelector((state) => state.space.moduleData);
  const currentUser = useSelector((state) => state.user.currentUser);
  const [msgs, setMsgs] = useState([]);
  const [message, setMessage] = useState("");
  const [newTaskName, setNewTaskName] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [showImageLoading, setShowImageLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [deletingImageId, setDeletingImageId] = useState("");
  const [limit, setLimit] = useState(3);
  const inputTaskRef = useRef();
  const textareaRef = useRef();
  const imageInputRef = useRef();

  const { fromSpaceId, fromStationId } = task;
  const attachments = task.attachments || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message) {
      alert("no message");
      return;
    }
    createMessageToTask(
      task.fromSpaceId,
      task.fromStationId,
      message,
      currentUser.uid,
      task.id
    );
    textareaRef.current.value = "";
    setMessage("");
  };

  useEffect(() => {
    const { fromSpaceId, fromStationId, id } = task;
    db.collection("space")
      .doc(fromSpaceId)
      .collection("stations")
      .doc(fromStationId)
      .collection("tasks")
      .doc("tasks")
      .collection("msg")
      /* .orderBy("created", "asc") */
      .where("taskId", "==", id)
      .limit(limit)
      .onSnapshot((msgsData) => {
        let list = [];
        msgsData.forEach((msg) => {
          list.push(msg.data());
        });
        setMsgs(list);
      });
  }, []);

  const handleChangeTask = async (e) => {
    e.preventDefault();
    try {
      setShowLoading(true);
      await changeTaskName(fromSpaceId, fromStationId, newTaskName, task.id);
    } catch (error) {
      console.log(error.message);
    } finally {
      setTimeout(() => {
        setShowLoading(false);
        inputTaskRef.current.blur();
      }, 500);
    }
  };

  const handleUploadTaskImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImageError("");
      setShowImageLoading(true);
      await uploadTaskImage(
        fromSpaceId,
        fromStationId,
        task.id,
        file,
        currentUser.uid
      );
    } catch (error) {
      setImageError(error.message);
    } finally {
      setShowImageLoading(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
      }
    }
  };

  const handleRemoveTaskImage = async (attachment) => {
    try {
      setImageError("");
      setDeletingImageId(attachment.id);
      await removeTaskImage(fromSpaceId, fromStationId, task.id, attachment);
    } catch (error) {
      setImageError(error.message);
    } finally {
      setDeletingImageId("");
    }
  };

  return (
    <div className="largeTask">
      <div className="lt__taskStuff">
        <form onSubmit={(e) => handleChangeTask(e)}>
          {showLoading && <Loading />}
          <input
            ref={inputTaskRef}
            placeholder={task.content}
            onChange={(e) => setNewTaskName(e.target.value)}
          />
        </form>
      </div>
      <div className="lt__content">
        <div className="lt__modules">
          {moduleData?.map((module) => {
            return <LoadModule module={module} key={module.name} task={task} />;
          })}
          <div className="lt__description">
            <TaskDescription description={task.description} taskId={task.id} />
          </div>
          <div className="lt__subtasks">
            <Subtasks task={task} open opacity="opacityTrue" />
          </div>
          <div className="lt__attachments">
            <div className="lt__attachments-header">
              <p>Images</p>
              <label
                className={`lt__attachments-upload ${
                  showImageLoading ? "lt__attachments-upload--disabled" : ""
                }`}
              >
                <FontAwesomeIcon icon={faImage} />
                <span>{showImageLoading ? "Uploading..." : "Add image"}</span>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  disabled={showImageLoading}
                  onChange={(e) => handleUploadTaskImage(e)}
                />
              </label>
            </div>
            {imageError && <p className="lt__attachments-error">{imageError}</p>}
            {attachments.length > 0 ? (
              <div className="lt__attachments-grid">
                {attachments.map((attachment) => (
                  <div className="lt__attachment" key={attachment.id}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noreferrer"
                      className="lt__attachment-image"
                    >
                      <img src={attachment.url} alt={attachment.name} />
                    </a>
                    <div className="lt__attachment-footer">
                      <p title={attachment.name}>{attachment.name}</p>
                      <button
                        type="button"
                        disabled={deletingImageId === attachment.id}
                        onClick={() => handleRemoveTaskImage(attachment)}
                        aria-label="Remove image"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="lt__attachments-empty">No images yet</p>
            )}
          </div>
        </div>

        <div className="lt__writeUpdate">
          <div className="lt__writeUpdate-msgs">
            {msgs.map((msg) => {
              return <Message msg={msg} />;
            })}
          </div>
          <div className="lt__writeUpdate-send">
            <div className="lt__writeUpdate-avatar">
              <Avatar src={currentUser.imageUrl} />
            </div>
            <div className="lt__writeUpdate-message">
              <form onSubmit={(e) => handleSubmit(e)}>
                <textarea
                  onChange={(e) => setMessage(e.target.value)}
                  ref={textareaRef}
                  placeholder="Write update"
                />
                <div className="lt__writeUpdate-btn">
                  <RetroButton>Submit</RetroButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LargeTask;
